import omit from 'lodash/omit';

import {BaseClient} from 'common/report/queries/utils/base-client';
import {RetentionType} from 'common/report/util/retention/types';

export interface ApiClient {
  runQuery(params: any, headers?: {[header: string]: string}, clearCache?: boolean): Promise<RetentionQueryResponse>;
}

export class RetentionApiClient extends BaseClient implements ApiClient {
  public buildEndpoint(params) {
    return `api/2.0/retention/${params.retention_type === `addiction` ? `frequency` : ``}`;
  }

  public buildRequestBody(params: any) {
    return JSON.stringify(omit(params, `retention_type`));
  }

  public async runQuery(params, headers?, clearCache = false): Promise<RetentionQueryResponse> {
    return this.query(params, {method: `POST`, headers, clearCache});
  }
}
export interface ErrorResponse {
  error: string;
  message?: string;
  data?: any;
}

export type Retention = {
  first: number;
  counts: number[];
};

export type Addiction = number[];

/** Wrapper for all successful responses */
type DataResponse<T> = {
  results: T;
  /** only for addiction, hack for now until we unify results and data in the api */
  data?: T;
  computed_at: string;
  metadata: {
    min_sampling_factor?: number;
    incomplete_buckets?: {
      [date: string]: Array<null | string>;
    };
  };
};

export type KeyedByDate<T> = {
  [date: string]: T;
};

export type KeyedBySegment<T> = {
  [segment: string]: T;
};

export type RecurringResponse = KeyedByDate<Retention>;
export type SegmentedRecurringResponse = KeyedByDate<KeyedBySegment<Retention>>;

export type AddictionResponse = KeyedByDate<Addiction>;
export type SegmentedAddictionResponse = KeyedByDate<KeyedBySegment<Addiction>>;

/**
 * A dumb type-checker for when you know the type but need to convince TS.
 * @param _r unused, only receives the type
 * @param flagOrType boolean or RetentionType; determines the outcome
 */
export function isAddictionResponse(
  _r: any,
  flagOrType: boolean | RetentionType,
): _r is AddictionResponse | SegmentedAddictionResponse {
  if (typeof flagOrType === `string`) {
    return flagOrType === `addiction`;
  } else if (typeof flagOrType === `boolean`) {
    return flagOrType;
  }

  return false;
}

/**
 * A dumb type-checker for when you know the type but need to convince TS.
 *
 * @param _r unused, only receives the type
 * @param flagOrType boolean or RetentionType; determines the outcome
 */

export function isRetentionResponse(
  _r: any,
  flagOrType: boolean | RetentionType,
): _r is RecurringResponse | SegmentedRecurringResponse {
  if (typeof flagOrType === `string`) {
    return flagOrType === `birth` || flagOrType === `compounded`;
  } else if (typeof flagOrType === `boolean`) {
    return flagOrType;
  }

  return false;
}

/**
 * A dumb type-checker for when you know the type but need to convince TS.
 *
 * @param _r not checked, receives the type
 * @param segmented whether the query was segmented
 */
export function isSegmented(
  _r: RetentionData,
  segmented: boolean,
): _r is SegmentedAddictionResponse | SegmentedRecurringResponse {
  return segmented;
}
/**
 * A dumb type-checker for when you know the type but need to convince TS.
 *
 * @param _r a response object, not used except as the recipient of the type
 * @param isUnsegmented determines the outcome
 */
export function isUnsegmentedData(_r: any, isUnsegmented: boolean): _r is RecurringResponse | AddictionResponse {
  return !!isUnsegmented;
}
export function isErrorResponse(r: RetentionQueryResponse): r is ErrorResponse {
  return `error` in r;
}

/** Type for representing "unwrapped" query responses */
export type RetentionData =
  | AddictionResponse
  | SegmentedAddictionResponse
  | RecurringResponse
  | SegmentedRecurringResponse;

/** All possible query api responses */
export type RetentionQueryResponse = ErrorResponse | DataResponse<RetentionData>;
export type SuccessResponse = Exclude<RetentionQueryResponse, ErrorResponse>;
